import { useEffect, useState } from 'react';
import type { TenantConfig } from '@/types/index';
import { resolveTenant } from '../tenants/index';

/**
 * Tenant resolution hook.
 *
 * Resolves once at mount based on subdomain (prod) or ?tenant= / VITE_TENANT_ID (dev).
 * Also fetches the live theme.json from /branding/{tenant}/theme.json and
 * applies the per-tenant CSS-vars to a wrapper element.
 *
 * Per spec §3 ClariCommand brand template system + Rev 2 brand-tokens-via-CSS-vars.
 */
export function useTenant(): TenantConfig {
  const [tenant] = useState<TenantConfig>(() => resolveTenant());

  useEffect(() => {
    // Fetch the per-tenant theme.json and apply CSS-vars
    fetch(`/branding/${tenant.tenantId}/theme.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((theme: { tokens?: Record<string, string> } | null) => {
        if (!theme?.tokens) return;
        const root = document.documentElement;
        for (const [key, value] of Object.entries(theme.tokens)) {
          root.style.setProperty(`--cdx-${key}`, value);
        }
      })
      .catch(() => {
        // Falls back to defaults baked into tailwind.config.ts
      });
    // Set favicon dynamically
    const favLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favLink) {
      favLink.href = `/branding/${tenant.tenantId}/favicon.svg`;
    }
    // Set title
    document.title = `${tenant.tenantName} · Reports Portal`;
  }, [tenant.tenantId, tenant.tenantName]);

  return tenant;
}
